import React, { useContext, createContext } from "react";
import { usePaywallIframeManager } from "./paywalliframeManager.context";
import { usePaywallStateManagerProvider } from "./paywallStateManager.context";
import {
  AuthStatus,
  EnhancedResource,
  SubscriptionDetails,
  TeamListItem,
} from "../../types";

export interface ParentPaywallProviderContextValue {
  showDashboard: () => void;
  showCreateProfile: () => void;
  showPricing: () => void;
  hide: () => void;
  // EXISTING - Events (unchanged for backward compatibility)
  onProfileCreated: (handler: () => void) => () => void;
  isAuthenticated: boolean;
  hasAccess: boolean;
  authStatus: AuthStatus;
  subscriptions: SubscriptionDetails[];
  resources: EnhancedResource[];
  ownedTeams: TeamListItem[];
}

const ParentPaywallContext =
  createContext<ParentPaywallProviderContextValue | null>(null);

export function useParentPaywallProvider(): ParentPaywallProviderContextValue {
  const context = useContext(ParentPaywallContext);
  if (!context) {
    throw new Error(
      "useParentPaywallProvider must be used within ParentPaywallProvider"
    );
  }
  return context;
}

export function ParentPaywallProvider({
  children,
}: React.PropsWithChildren<{}>) {
  const {
    showDashboard,
    showCreateProfile,
    showPricing,
    hide,
    // EXISTING - Events (unchanged for backward compatibility)
    onProfileCreated,
  } = usePaywallIframeManager();

  const { authStatus, resources, hasAccess, subscriptions, ownedTeams } =
    usePaywallStateManagerProvider();

  // Basic context without unified state (will be enhanced by PaywallStateManager)
  const contextValue: ParentPaywallProviderContextValue = React.useMemo(
    () => ({
      showDashboard,
      showCreateProfile,
      showPricing,
      hide,
      onProfileCreated,
      isAuthenticated: authStatus === "authenticated",
      authStatus,
      hasAccess,
      subscriptions,
      resources,
      ownedTeams,
    }),
    [
      showDashboard,
      showCreateProfile,
      showPricing,
      hide,
      onProfileCreated,
      authStatus,
      hasAccess,
      subscriptions,
      resources,
      ownedTeams,
    ]
  );

  return (
    <ParentPaywallContext.Provider value={contextValue}>
      {children}
    </ParentPaywallContext.Provider>
  );
}
